import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSpinner,
  faTimes,
  faArrowLeft,
  faSave,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

export default function SchemeFeatureEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState("");

  // Default empty state structure
  const defaultFormState = {
    featureName: "",
    schemeFeatureDescriptionKey: "",
    categoryId: "",
    schemeFeatureDefinition: "",
    schemeDescriptions: {},
    panelMemberPanelItem: {
      PanelMemberPanelItemCode: "",
      PanelMemberPanelItemLines: [],
    },
  };

  const [formData, setFormData] = useState(defaultFormState);

  useEffect(() => {
    Promise.all([
      fetchCategories(),
      id ? fetchSchemeFeature() : Promise.resolve(),
    ]).finally(() => {
      setLoading(false);
    });
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await fetch("/api/categories", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) {
      setError("Category name cannot be empty");
      return;
    }

    try {
      const response = await fetch("/api/categories", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ name: newCategory.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create category");
      }

      const createdCategory = await response.json();
      setCategories((prev) => [...prev, createdCategory]);
      setFormData((prev) => ({
        ...prev,
        categoryId: createdCategory.id,
      }));
      setNewCategory("");
      setShowNewCategoryInput(false);
      setSuccess("Category created successfully");
    } catch (error) {
      setError(error.message);
    }
  };

  const fetchSchemeFeature = async () => {
    try {
      const response = await fetch(`/api/scheme-description/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch scheme feature");
      }
      const data = await response.json();
      // Safely set form data with defaults for missing properties
      setFormData({
        featureName: data.featureName || "",
        schemeFeatureDescriptionKey: data.schemeFeatureDescriptionKey || "",
        categoryId: data.category || "",
        schemeFeatureDefinition: data.schemeFeatureDefinition || "",
        schemeDescriptions: data.schemeDescriptions || {},
        panelMemberPanelItem: {
          PanelMemberPanelItemCode:
            data.panelMemberPanelItem?.PanelMemberPanelItemCode || "",
          PanelMemberPanelItemLines: Array.isArray(
            data.panelMemberPanelItem?.PanelMemberPanelItemLines
          )
            ? data.panelMemberPanelItem.PanelMemberPanelItemLines.map(
                (line) => ({
                  PanelItemLine: line.PanelItemLine || "",
                  PanelItemLineFormat: line.PanelItemLineFormat || "",
                  id: line.id || null,
                })
              )
            : [],
        },
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = [];

    // Validate Feature Name
    if (!formData.featureName) {
      errors.push("Feature Name is required");
    }

    // Validate Feature Key
    if (!formData.schemeFeatureDescriptionKey) {
      errors.push("Feature Key is required");
    } else if (formData.schemeFeatureDescriptionKey.length < 7) {
      errors.push("Feature Key must be at least 7 characters long");
    }

    // Validate Category
    if (!formData.categoryId) {
      errors.push("Category is required");
    }

    // Validate Definition
    if (!formData.schemeFeatureDefinition) {
      errors.push("Definition is required");
    }

    // Validate Descriptions
    Object.entries(formData.schemeDescriptions).forEach(
      ([type, descriptions]) => {
        descriptions.forEach((desc, index) => {
          if (desc.trim() && desc.length < 10) {
            errors.push(
              `Description under "${type}" at position ${index +
                1} must be at least 10 characters long`
            );
          }
        });
      }
    );

    // Validate Panel Member Item
    if (formData.panelMemberPanelItem.PanelMemberPanelItemCode) {
      if (formData.panelMemberPanelItem.PanelMemberPanelItemCode.length > 255) {
        errors.push("Panel Item Code must not exceed 255 characters");
      }

      // Validate Panel Lines if Panel Item Code exists
      formData.panelMemberPanelItem.PanelMemberPanelItemLines.forEach(
        (line, index) => {
          if (!line.PanelItemLine) {
            errors.push(`Panel Line at position ${index + 1} is required`);
          }
        }
      );
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate form
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join("\n"));
      }

      // Find the category ID from the selected category name
      const selectedCategory = categories.find(
        (cat) => cat.name === formData.categoryId
      );
      if (!selectedCategory) {
        throw new Error("Invalid category selected");
      }

      // First, update the main scheme feature
      const schemeFeatureResponse = await fetch(
        id ? `/api/scheme-features/${id}` : "/api/scheme-features",
        {
          method: id ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            featureName: formData.featureName.trim(),
            schemeFeatureDescriptionKey: formData.schemeFeatureDescriptionKey.trim(),
            categoryId: selectedCategory.id,
            schemeFeatureDefinition: formData.schemeFeatureDefinition.trim(),
          }),
        }
      );

      if (!schemeFeatureResponse.ok) {
        const errorData = await schemeFeatureResponse.json();
        throw new Error(errorData.message || "Failed to save scheme feature");
      }

      const savedSchemeFeature = await schemeFeatureResponse.json();

      // Only process descriptions if they exist and are valid
      if (Object.keys(formData.schemeDescriptions).length > 0) {
        const validDescriptions = Object.entries(
          formData.schemeDescriptions
        ).flatMap(([type, descriptions]) =>
          descriptions
            .filter((desc) => desc.trim().length >= 10)
            .map((description) => ({
              schemeFeatureId: savedSchemeFeature.id,
              type,
              description: description.trim(),
            }))
        );

        if (validDescriptions.length > 0) {
          for (const desc of validDescriptions) {
            const response = await fetch("/api/descriptions", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              body: JSON.stringify(desc),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(
                errorData.message || "Failed to save description"
              );
            }
          }
        }
      }

      // Only process panel member item if code exists and is valid
      if (
        formData.panelMemberPanelItem.PanelMemberPanelItemCode.trim() &&
        formData.panelMemberPanelItem.PanelMemberPanelItemCode.length <= 255
      ) {
        const panelItemResponse = await fetch("/api/panel-members", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            schemeFeatureId: savedSchemeFeature.id,
            panelMemberPanelItemCode: formData.panelMemberPanelItem.PanelMemberPanelItemCode.trim(),
          }),
        });

        if (!panelItemResponse.ok) {
          throw new Error("Failed to save panel item");
        }

        const savedPanelItem = await panelItemResponse.json();

        // Only process valid lines
        const validLines = formData.panelMemberPanelItem.PanelMemberPanelItemLines.filter(
          (line) => line.PanelItemLine.trim()
        ).map((line) => ({
          panelMemberItemId: savedPanelItem.id,
          panelItemLine: line.PanelItemLine.trim(),
          panelItemLineFormat: line.PanelItemLineFormat?.trim() || null,
        }));

        if (validLines.length > 0) {
          const linePromises = validLines.map((line) =>
            fetch(`/api/panel-members/${savedPanelItem.id}/lines`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              body: JSON.stringify(line),
            })
          );

          await Promise.all(linePromises);
        }
      }

      setSuccess("Scheme feature saved successfully");
    } catch (error) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "new") {
      setShowNewCategoryInput(true);
    } else {
      setFormData((prev) => ({
        ...prev,
        categoryId: value,
      }));
    }
  };

  const handleSchemeDescriptionChange = (key, index, value) => {
    setFormData((prev) => ({
      ...prev,
      schemeDescriptions: {
        ...prev.schemeDescriptions,
        [key]: prev.schemeDescriptions[key].map((desc, i) =>
          i === index ? value : desc
        ),
      },
    }));
  };

  const addSchemeDescription = (key) => {
    setFormData((prev) => ({
      ...prev,
      schemeDescriptions: {
        ...prev.schemeDescriptions,
        [key]: [...(prev.schemeDescriptions[key] || []), ""],
      },
    }));
  };

  const removeSchemeDescription = async (key, index, descriptionId) => {
    if (
      descriptionId &&
      !window.confirm("Are you sure you want to remove this description?")
    ) {
      return;
    }

    if (descriptionId) {
      try {
        const response = await fetch(`/api/descriptions/${descriptionId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete description");
        }
      } catch (error) {
        setError(error.message);
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      schemeDescriptions: {
        ...prev.schemeDescriptions,
        [key]: prev.schemeDescriptions[key].filter((_, i) => i !== index),
      },
    }));
  };

  const addNewDescriptionType = () => {
    const newType = window.prompt("Enter new description type:");
    if (newType && newType.trim()) {
      setFormData((prev) => ({
        ...prev,
        schemeDescriptions: {
          ...prev.schemeDescriptions,
          [newType.trim()]: [],
        },
      }));
    }
  };

  const removeDescriptionType = (key) => {
    if (
      window.confirm(
        `Are you sure you want to remove "${key}" description type?`
      )
    ) {
      setFormData((prev) => {
        const newDescriptions = { ...prev.schemeDescriptions };
        delete newDescriptions[key];
        return {
          ...prev,
          schemeDescriptions: newDescriptions,
        };
      });
    }
  };

  const handlePanelItemCodeChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      panelMemberPanelItem: {
        ...prev.panelMemberPanelItem,
        PanelMemberPanelItemCode: e.target.value,
      },
    }));
  };

  const handlePanelLineChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      panelMemberPanelItem: {
        ...prev.panelMemberPanelItem,
        PanelMemberPanelItemLines: prev.panelMemberPanelItem.PanelMemberPanelItemLines.map(
          (line, i) => (i === index ? { ...line, [field]: value } : line)
        ),
      },
    }));
  };

  const addPanelLine = () => {
    setFormData((prev) => ({
      ...prev,
      panelMemberPanelItem: {
        ...prev.panelMemberPanelItem,
        PanelMemberPanelItemLines: [
          ...prev.panelMemberPanelItem.PanelMemberPanelItemLines,
          { PanelItemLine: "", PanelItemLineFormat: "" },
        ],
      },
    }));
  };

  const removePanelLine = async (index, lineId) => {
    if (
      lineId &&
      !window.confirm("Are you sure you want to remove this line?")
    ) {
      return;
    }

    if (lineId) {
      try {
        const response = await fetch(
          `/api/panel-members/${formData.panelMemberPanelItem.id}/lines/${lineId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete panel line");
        }
      } catch (error) {
        setError(error.message);
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      panelMemberPanelItem: {
        ...prev.panelMemberPanelItem,
        PanelMemberPanelItemLines: prev.panelMemberPanelItem.PanelMemberPanelItemLines.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const resetForm = () => {
    setFormData(defaultFormState);
    setError(null);
    setSuccess(null);
  };

  // Add a cancel handler that resets the form
  const handleCancel = () => {
    if (
      window.confirm(
        "Are you sure you want to cancel? All changes will be lost."
      )
    ) {
      resetForm();
      navigate("/admin/dashboard/scheme-features");
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading scheme feature...</p>
      </div>
    );
  }

  return (
    <div className="scheme-feature-edit">
      <div className="edit-header">
        <button
          className="back-btn"
          onClick={() => navigate("/admin/dashboard/scheme-features")}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        <h2>{id ? "Edit" : "Add"} Scheme Feature</h2>
      </div>

      {error && (
        <div className="message-box error">
          <span>
            <FontAwesomeIcon icon={faExclamationCircle} />
            {error}
          </span>
          <button onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {success && (
        <div className="message-box success">
          <span>
            <FontAwesomeIcon icon={faCheckCircle} />
            {success}
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="edit-form">
        <div className="form-sections">
          <section className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label htmlFor="featureName">Feature Name</label>
              <input
                type="text"
                id="featureName"
                name="featureName"
                value={formData.featureName}
                onChange={handleInputChange}
                required
                placeholder="Enter a descriptive name"
              />
              <small className="help-text">
                A user-friendly name for the feature
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="schemeFeatureDescriptionKey">Feature Key</label>
              <input
                type="text"
                id="schemeFeatureDescriptionKey"
                name="schemeFeatureDescriptionKey"
                value={formData.schemeFeatureDescriptionKey}
                onChange={handleInputChange}
                required
                placeholder="Enter feature key (min 7 characters)"
              />
              <small className="help-text">
                Must be at least 7 characters long
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="categoryId">Category</label>
              {showNewCategoryInput ? (
                <div className="new-category-input">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Enter new category name"
                  />
                  <small className="help-text">
                    Category name must be at least 1 character
                  </small>
                  <div className="button-group">
                    <button
                      type="button"
                      className="save-btn"
                      onClick={handleAddNewCategory}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} /> Save
                    </button>
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategory("");
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <select
                    id="categoryId"
                    value={formData.categoryId || ""}
                    onChange={handleCategoryChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                    <option value="new">+ Add New Category</option>
                  </select>
                  <small className="help-text">
                    Select an existing category or add a new one
                  </small>
                </>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="schemeFeatureDefinition">Definition</label>
              <textarea
                id="schemeFeatureDefinition"
                name="schemeFeatureDefinition"
                value={formData.schemeFeatureDefinition}
                onChange={handleInputChange}
                required
              />
              <small className="help-text">
                Required field - Provide a clear definition
              </small>
            </div>
          </section>

          <section className="form-section">
            <div className="section-header">
              <h3>Scheme Descriptions</h3>
              <button
                type="button"
                onClick={addNewDescriptionType}
                className="add-btn"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Description Type
              </button>
            </div>
            <small className="help-text section-help">
              Each description must be at least 10 characters long
            </small>
            {Object.entries(formData.schemeDescriptions).map(
              ([key, descriptions]) => (
                <div key={key} className="description-group">
                  <div className="description-header">
                    <h4>{key}</h4>
                    <button
                      type="button"
                      onClick={() => removeDescriptionType(key)}
                      className="remove-btn"
                      title="Remove this description type"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                  {descriptions.map((desc, index) => (
                    <div key={index} className="description-item">
                      <input
                        type="text"
                        value={desc.description}
                        onChange={(e) =>
                          handleSchemeDescriptionChange(
                            key,
                            index,
                            e.target.value
                          )
                        }
                        placeholder="Enter description (min 10 characters)"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          removeSchemeDescription(key, index, desc.id)
                        }
                        className="remove-btn"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addSchemeDescription(key)}
                    className="add-description-btn"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Description
                  </button>
                </div>
              )
            )}
          </section>

          <section className="form-section">
            <h3>Panel Member Item</h3>
            <small className="help-text section-help">
              Optional section - Fill this if you want to add panel member
              details
            </small>
            <div className="form-group">
              <label htmlFor="panelItemCode">Panel Item Code</label>
              <input
                type="text"
                id="panelItemCode"
                value={formData.panelMemberPanelItem.PanelMemberPanelItemCode}
                onChange={handlePanelItemCodeChange}
                placeholder="Enter panel item code"
              />
              <small className="help-text">Maximum 255 characters</small>
            </div>

            <div className="panel-lines">
              <div className="section-header">
                <h4>Panel Lines</h4>
                <button
                  type="button"
                  onClick={addPanelLine}
                  className="add-btn"
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Line
                </button>
              </div>
              <small className="help-text section-help">
                Panel lines are required if panel item code is provided
              </small>
              {formData.panelMemberPanelItem.PanelMemberPanelItemLines.map(
                (line, index) => (
                  <div key={index} className="panel-line-item">
                    <div className="form-group">
                      <input
                        type="text"
                        value={line.PanelItemLine}
                        onChange={(e) =>
                          handlePanelLineChange(
                            index,
                            "PanelItemLine",
                            e.target.value
                          )
                        }
                        placeholder="Enter panel line (required)"
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        value={line.PanelItemLineFormat}
                        onChange={(e) =>
                          handlePanelLineChange(
                            index,
                            "PanelItemLineFormat",
                            e.target.value
                          )
                        }
                        placeholder="Enter line format (optional)"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removePanelLine(index, line.id)}
                      className="remove-btn"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                )
              )}
            </div>
          </section>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="save-btn" disabled={saving}>
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} /> Save Changes
              </>
            )}
          </button>
        </div>
      </form>

      <style jsx>{`
        /* Reset and Base Styles */
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        /* Layout & Structure */
        .scheme-feature-edit {
          max-width: 1200px;
          margin: 0 auto;
          background: #ffffff;
        }

        .edit-header {
          display: flex;
          align-items: center;
          flex-direction: row-reverse;
          justify-content: space-between;
          gap: 2rem;
          margin-bottom: 2.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #e2e2e2;
          padding: 2rem;
        }

        .edit-header h2 {
          font-size: 1.75rem;
          font-weight: 500;
          margin: 0;
        }

        /* Form Sections */
        .form-sections {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
          margin-bottom: 2.5rem;
          width: 100%;
        }

        .form-section {
          background: #ffffff;
          padding: 2.5rem;
          border-radius: 8px;
          border: 1px solid #e2e2e2;
          width: 100%;
        }

        .form-section h3 {
          color: #000000;
          font-size: 1.25rem;
          margin: 0 0 1.5rem 0;
          font-weight: 500;
        }

        /* Form Groups */
        .form-group {
          margin-bottom: 2rem;
          width: 100%;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 1rem;
          border: 1px solid #e2e2e2;
          border-radius: 6px;
          font-size: 1rem;
          line-height: 1.5;
          transition: all 0.2s ease;
          background: #ffffff;
          min-height: 48px;
        }

        /* Description Groups */
        .description-group {
          background: #f6f6f6;
          padding: 2rem;
          border-radius: 6px;
          margin-bottom: 1.5rem;
          border: 1px solid #e2e2e2;
          width: 100%;
        }

        .description-item {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
          width: 100%;
        }

        .description-item input {
          flex: 1;
          min-height: 48px;
        }

        /* Panel Lines */
        .panel-lines {
          margin-top: 2.5rem;
        }

        .panel-lines .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .panel-lines .help-text {
          display: block;
          font-size: 0.875rem;
          color: #545454;
          margin: 1rem 0 1.5rem;
          padding: 0.875rem;
          background: #f6f6f6;
          border-radius: 6px;
          border-left: 4px solid #000000;
        }

        .panel-line-item {
          display: grid;
          grid-template-columns: minmax(300px, 2fr) minmax(200px, 1fr) auto;
          gap: 1.5rem;
          align-items: start;
          background: #f6f6f6;
          padding: 1.5rem;
          border-radius: 6px;
          margin-bottom: 1.5rem;
          border: 1px solid #e2e2e2;
          width: 100%;
        }

        .panel-line-item:last-child {
          margin-bottom: 0;
        }

        .panel-line-item .form-group {
          margin-bottom: 0;
        }

        /* Buttons */
        .back-btn,
        .add-btn,
        .save-btn,
        .cancel-btn,
        .remove-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid transparent;
        }

        .back-btn {
          background: none;
          border-color: #e2e2e2;
          color: #000000;
        }

        .add-btn,
        .save-btn {
          background: #000000;
          color: #ffffff;
        }

        .cancel-btn {
          background: #ffffff;
          border-color: #e2e2e2;
          color: #000000;
        }

        .remove-btn {
          padding: 0.5rem;
          background: none;
          color: #545454;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .remove-btn:hover {
          background: rgba(0, 0, 0, 0.05);
          color: #000000;
        }

        .message-box .remove-btn {
          padding: 0.25rem;
          width: 24px;
          height: 24px;
        }

        .description-header .remove-btn {
          opacity: 0.7;
        }

        .description-header .remove-btn:hover {
          opacity: 1;
        }

        .panel-line-item .remove-btn {
          align-self: center;
        }

        /* Form Actions */
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 2.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e2e2e2;
        }

        .form-actions button {
          min-width: 120px;
        }

        /* Help Text */
        .help-text {
          display: block;
          font-size: 0.875rem;
          color: #545454;
          margin-top: 0.5rem;
        }

        .section-help {
          margin: 1rem 0;
          padding: 0.875rem;
          background: #f6f6f6;
          border-radius: 6px;
          border-left: 4px solid #000000;
        }

        /* Messages */
        .message-box {
          padding: 1rem;
          border-radius: 6px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          font-size: 0.875rem;
        }

        .message-box.error {
          background: #fff0f0;
          border-left: 4px solid #000000;
          color: #000000;
        }

        .message-box.success {
          background: #f0fff4;
          border-left: 4px solid #000000;
          color: #000000;
        }

        /* Responsive Design */
        @media (max-width: 1400px) {
          .scheme-feature-edit {
            max-width: 1000px;
          }
        }

        @media (max-width: 1200px) {
          .scheme-feature-edit {
            max-width: 90%;
          }

          .form-section {
            padding: 2rem;
          }
        }

        @media (max-width: 768px) {
          .scheme-feature-edit {
            max-width: 100%;
          }

          .form-section {
            padding: 1.5rem;
          }

          .panel-line-item {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .panel-line-item .form-group {
            margin-bottom: 1rem;
          }

          .panel-line-item .form-group:last-child {
            margin-bottom: 0;
          }

          .description-item {
            flex-direction: column;
            gap: 0.75rem;
          }

          .description-item input {
            width: 100%;
          }

          .remove-btn {
            width: fit-content;
          }
        }

        @media (max-width: 480px) {
          .scheme-feature-edit {
          }

          .form-section {
            padding: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
}
